import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Disc3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import useSubscription from '@/hooks/useSubscription';

/*
 * BillingSuccess
 *
 * Landing page after Stripe Checkout. Polls /api/billing/checkout/status/{id}
 * (which internally calls Stripe) to flip the in-app subscription from
 * 'trialing' to 'active'. Polls up to 10 times at 2s intervals so a 20-second
 * webhook delay is fine — we'll see it before the timeout.
 *
 * Once active, refreshes the auth context so /auth/me reflects the new
 * subscription, then offers a quick path back to the Collection.
 */
export default function BillingSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | failed | timeout
  const [attempts, setAttempts] = useState(0);
  const { refresh } = useSubscription();

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return undefined;
    }
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 10;
    const interval = 2000;

    const poll = async () => {
      if (cancelled) return;
      attempt += 1;
      setAttempts(attempt);
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data?.payment_status === 'paid' || data?.status === 'complete') {
          if (!cancelled) {
            setStatus('paid');
            try { await refresh(); } catch (_) {
              // Non-fatal — /auth/me will pick up subscription on next nav
            }
          }
          return;
        }
        if (data?.status === 'expired') {
          if (!cancelled) setStatus('failed');
          return;
        }
      } catch (_) {
        // soft-fail and retry
      }
      if (attempt >= maxAttempts) {
        if (!cancelled) setStatus('timeout');
        return;
      }
      setTimeout(poll, interval);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-md mx-auto py-12 px-4 text-center" data-testid="billing-success-page">
      {status === 'polling' && (
        <>
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-[hsl(var(--primary))]" />
          <h1 className="font-display text-2xl font-semibold mt-4">Confirming your subscription…</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Hang tight — finalising things with Stripe. ({attempts}/10)
          </p>
        </>
      )}
      {status === 'paid' && (
        <>
          <div className="mx-auto w-14 h-14 rounded-2xl bg-[hsl(var(--primary))] grid place-items-center text-[hsl(var(--primary-foreground))]">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-4" data-testid="billing-success-headline">
            Welcome to GroovLens Pro
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            You&rsquo;re all set. Unlimited scans, AI deep dives, and your
            crates are now yours forever.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Button onClick={() => navigate('/crate')} className="rounded-xl" data-testid="billing-success-collection-button">
              <Disc3 className="w-4 h-4 mr-1.5" /> Go to my Collection
            </Button>
            <Button variant="secondary" onClick={() => navigate('/upload')} className="rounded-xl">
              Scan more screenshots
            </Button>
          </div>
        </>
      )}
      {(status === 'failed' || status === 'timeout') && (
        <>
          <h1 className="font-display text-2xl font-semibold">We&rsquo;re still working on it</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Stripe is taking a moment to confirm. Your subscription will appear
            in your Profile shortly. You can refresh this page or head back
            to your Collection.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Button onClick={() => window.location.reload()} className="rounded-xl">Check again</Button>
            <Button variant="secondary" onClick={() => navigate('/crate')} className="rounded-xl">Go to Collection</Button>
          </div>
        </>
      )}
    </div>
  );
}
